import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  Directive,
  ElementRef,
  HostListener,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import {
  defaultDropdownPositionPairs,
  defaultTooltipPositionPairs,
} from './cdk-overlay-position-pairs';

@Directive({
  selector: '[poziOverlay]',
  exportAs: 'overlay',
})
export class OverlayDirective implements OnDestroy {
  private overlay = inject(Overlay);
  private elementRef = inject(ElementRef);
  private viewContainerRef = inject(ViewContainerRef);

  trigger = input<'click' | 'hover'>('hover');
  template = input<TemplateRef<any> | null | undefined>();
  templateContext = input<any>({});
  /**
   * Marker class put on the CDK pane **in addition to** the `cdk-tooltip` /
   * `cdk-dropdown` class the trigger implies, so a consumer can key its own
   * `::ng-deep .cdk-overlay-pane…` rules on its own panes. The pane is created
   * by the CDK under `<body>` and carries no `_ngcontent` attribute, so a class
   * on the pane is the only scoping seam a component sheet has.
   *
   * It is additive on purpose: the two implied classes carry the shared pane
   * theming (`styles.scss`, `styles/_tooltip-theme.scss`), which a replacing
   * class would silently drop.
   */
  overlayClass = input<string>();
  cdkOverlayPositionPairs = input<ConnectedPosition[]>();

  private isOpen = signal(false);
  private overlayRef: OverlayRef | null = null;

  private panelClass = computed<string[]>(() => {
    const implied = this.isTooltip() ? 'cdk-tooltip' : 'cdk-dropdown';
    const extra = this.overlayClass();
    return extra ? [implied, extra] : [implied];
  });

  private positionPairs = computed<ConnectedPosition[]>(
    () =>
      this.cdkOverlayPositionPairs() ||
      (this.isTooltip()
        ? defaultTooltipPositionPairs
        : defaultDropdownPositionPairs)
  );

  constructor() {
    effect(() => {
      if (this.isOpen()) {
        this.show();
      } else {
        this.hide();
      }
    });
  }

  @HostListener('mouseenter') onMouseEnter() {
    if (this.template() && this.isTooltip()) this.isOpen.set(true);
  }

  @HostListener('mouseleave') onMouseLeave() {
    if (this.template() && this.isTooltip()) this.isOpen.set(false);
  }

  @HostListener('click') onClick() {
    if (this.template() && !this.isTooltip()) this.isOpen.update((o) => !o);
  }

  private show() {
    if (this.overlayRef || !this.template()) return;

    this.overlayRef = this.createOverlayRef();
    this.addOutsideClickEventListener();
    this.addDetachmentsListener();
    this.setTriggerWidthCSSVar();

    const portal = new TemplatePortal(
      this.template()!,
      this.viewContainerRef,
      this.templateContext()
    );

    this.overlayRef?.attach(portal);
    this.addVisibleClassToTooltip();
  }

  private createOverlayRef(): OverlayRef {
    return this.overlay.create({
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.elementRef)
        .withPositions(this.positionPairs()),
      panelClass: this.panelClass(),
      hasBackdrop: false,
      scrollStrategy: this.overlay.scrollStrategies.close(),
    });
  }

  private addOutsideClickEventListener() {
    if (!this.isTooltip()) {
      this.overlayRef?.outsidePointerEvents().subscribe((e) => {
        if (!this.elementRef.nativeElement.contains(e.target)) {
          this.isOpen.set(false);
        }
      });
    }
  }

  private addDetachmentsListener() {
    this.overlayRef?.detachments().subscribe(() => {
      if (this.isOpen()) {
        this.isOpen.set(false);
      }
    });
  }

  private setTriggerWidthCSSVar() {
    if (this.isTooltip()) {
      this.overlayRef?.overlayElement?.style.setProperty(
        '--triggerElWidth',
        `${(this.elementRef.nativeElement as HTMLElement).clientWidth}`
      );
    }
  }

  private addVisibleClassToTooltip() {
    if (this.isTooltip()) {
      requestAnimationFrame(() => {
        this.overlayRef?.overlayElement?.classList.add('visible');
      });
    }
  }

  private isTooltip(): boolean {
    return this.trigger() === 'hover';
  }

  private hide() {
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  destroy() {
    this.isOpen.set(false);
    this.hide();
  }

  ngOnDestroy() {
    this.destroy();
  }
}
